import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ExtensionsPanel, { type ExtensionItem } from "@/components/ExtensionsPanel";
import { getAllFeatures, type Feature } from "@/api/featuresApi";
import { formatDate } from "@/utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface Product {
	created_at: string;
	package_name?: string;
	allowed_users?: number;
	current_users?: number;
	active_users?: number;
}

interface ProductCardProps {
	product: Product;
	enabledFeatureKeys?: string[];
}

const ProductCard = ({ product, enabledFeatureKeys = [] }: ProductCardProps) => {
	const navigate = useNavigate();
	const { org_id } = useParams();
	const [availableFeatures, setAvailableFeatures] = useState<Feature[]>([]);

	useEffect(() => {
		let isMounted = true;
		const controller = new AbortController();

		getAllFeatures(controller.signal)
			.then((response) => {
				if (isMounted) {
					setAvailableFeatures(response.data.data);
				}
			})
			.catch((error) => {
				console.error("Error fetching features for product grid:", error);
			});

		return () => {
			isMounted = false;
			controller.abort();
		};
	}, []);

	const activeFeatures = useMemo<ExtensionItem[]>(() => {
		const featureByKey = new Map(availableFeatures.map((f) => [f.key, f]));
		return enabledFeatureKeys.map((key) => {
			const feature = featureByKey.get(key);
			return { name: feature?.name || key, category: feature?.category };
		});
	}, [availableFeatures, enabledFeatureKeys]);

	return (
		<Card
			onClick={() => {
				navigate(`/org/${org_id}/product/1`);
			}}
			className="cursor-pointer max-w-6xl"
		>
			<div className="flex">
				<div className="flex-1">
					<CardHeader>
						<CardTitle>Property</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-1">
							<p className="text-sm text-gray-500">
								Ημ. Δημιουργίας:{" "}
								{formatDate(product.created_at, { simple: true, withTime: true })}
							</p>
							<p>
								<span className="font-semibold">Πακέτο ενεργής συνδρομής:</span>{" "}
								{product?.package_name}
							</p>
							<p>
								<span className="font-semibold">Μέγιστος αριθμός χρηστών:</span>{" "}
								{product?.allowed_users}
							</p>
							<p>
								<span className="font-semibold">Συνολικοί χρήστες:</span>{" "}
								{product?.current_users}
							</p>
							<p>
								<span className="font-semibold">Ενεργοί χρήστες:</span>{" "}
								{product?.active_users}
							</p>
						</div>
					</CardContent>
				</div>
				<div className="w-px shrink-0 self-stretch bg-gray-200 my-5" />
				<ExtensionsPanel features={activeFeatures} totalCount={availableFeatures.length} className="flex-[1] min-w-0" />
			</div>
		</Card>
	);
};

const ProductGrid = ({
	product,
	enabledFeatureKeys,
}: {
	product: Product;
	enabledFeatureKeys?: string[];
}) => (
	<div>
		<ProductCard key={1} product={product} enabledFeatureKeys={enabledFeatureKeys} />
	</div>
);

export default ProductGrid;
